import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Loader2, Plus, Rocket, ArrowRight, CheckCircle2, Circle, Trash2, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

export default function ExpandAgency() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [tax, setTax] = useState(null);
  const [projects, setProjects] = useState([]);
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ from_state: "", to_state: "", business_type: "", target_date: "" });
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(() => { api.get("/me/expansions").then((r) => setProjects(r.data)).catch(() => {}); }, []);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    api.get("/compliance/taxonomy").then((r) => setTax(r.data)).catch(() => {});
    load();
  }, [user, loading, navigate, load]);

  if (loading || !user) return <div className="py-32 grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-blue-800" /></div>;

  const create = async (e) => {
    e.preventDefault();
    if (!form.from_state || !form.to_state) { toast.error("Select both states"); return; }
    setCreating(true);
    try {
      const { data } = await api.post("/me/expansions", form);
      toast.success("Expansion project created"); setShowForm(false); setForm({ from_state: "", to_state: "", business_type: "", target_date: "" });
      load(); openProject(data.id);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not create"); }
    finally { setCreating(false); }
  };
  const openProject = async (id) => { const { data } = await api.get(`/me/expansions/${id}`); setActive(data); };
  const toggle = async (taskId) => { const { data } = await api.patch(`/me/expansions/${active.id}/task/${taskId}`); setActive(data); load(); };
  const remove = async (id) => { await api.delete(`/me/expansions/${id}`); toast.success("Deleted"); setActive(null); load(); };

  const sections = active ? [...new Set(active.tasks.map((t) => t.section))] : [];

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center w-12 h-12 rounded-xl bg-blue-900 text-teal-400"><Rocket className="w-6 h-6" /></span>
          <div><h1 className="font-display text-3xl font-bold text-blue-950">Expand My Agency</h1><p className="text-slate-500">Plan and track your multi-state expansion.</p></div>
        </div>
        <Button onClick={() => { setShowForm(!showForm); setActive(null); }} data-testid="new-expansion-btn" className="bg-teal-600 hover:bg-teal-700 text-white gap-2"><Plus className="w-4 h-4" /> New expansion</Button>
      </div>

      {showForm && (
        <form onSubmit={create} data-testid="expansion-form" className="bg-white rounded-2xl p-6 border border-blue-900/5 shadow-sm mb-8 grid sm:grid-cols-2 gap-4">
          <div><Label>Current state</Label>
            <Select value={form.from_state} onValueChange={(v) => setForm({ ...form, from_state: v })}>
              <SelectTrigger data-testid="exp-from" className="mt-1"><SelectValue placeholder="From…" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Target state</Label>
            <Select value={form.to_state} onValueChange={(v) => setForm({ ...form, to_state: v })}>
              <SelectTrigger data-testid="exp-to" className="mt-1"><SelectValue placeholder="To…" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.states.map((s) => <SelectItem key={s.slug} value={s.slug}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Business type</Label>
            <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
              <SelectTrigger data-testid="exp-biztype" className="mt-1"><SelectValue placeholder="Select…" /></SelectTrigger>
              <SelectContent className="max-h-72">{tax?.org_types.map((o) => <SelectItem key={o.id} value={o.name}>{o.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Target launch date</Label><Input type="date" data-testid="exp-date" value={form.target_date} onChange={(e) => setForm({ ...form, target_date: e.target.value })} className="mt-1" /></div>
          <Button type="submit" disabled={creating} data-testid="exp-create" className="sm:col-span-2 bg-blue-900 hover:bg-blue-950 text-white">{creating ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create expansion project"}</Button>
        </form>
      )}

      {!active && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="expansion-list">
          {projects.length === 0 && !showForm && <div className="sm:col-span-3 text-center py-16 bg-white rounded-2xl border border-blue-900/5"><Rocket className="w-12 h-12 mx-auto text-slate-300 mb-3" /><p className="text-slate-500">No expansion projects yet. Start one to get a full launch checklist.</p></div>}
          {projects.map((p) => (
            <button key={p.id} data-testid={`expansion-card-${p.id}`} onClick={() => openProject(p.id)} className="text-left bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm hover:border-teal-500 hover:shadow-md transition-all">
              <p className="text-xs uppercase tracking-wide text-teal-700 font-semibold flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {p.from_state_name} → {p.to_state_name}</p>
              <p className="font-display font-bold text-blue-950 mt-1">{p.business_type || "Expansion project"}</p>
              <div className="mt-3 h-2 rounded-full bg-blue-100 overflow-hidden"><div className="h-full bg-teal-600 transition-all" style={{ width: `${p.progress}%` }} /></div>
              <p className="text-xs text-slate-500 mt-1.5">{p.progress}% complete</p>
            </button>
          ))}
        </div>
      )}

      {active && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} data-testid="expansion-detail">
          <button onClick={() => setActive(null)} className="text-sm text-blue-800 mb-4">← All projects</button>
          <div className="bg-blue-950 text-white rounded-2xl p-6 mb-6 flex items-center justify-between flex-wrap gap-4">
            <div>
              <p className="text-xs uppercase tracking-wide text-teal-400 font-semibold">Expand from {active.from_state_name} → {active.to_state_name}</p>
              <h2 className="font-display text-2xl font-bold mt-1">{active.business_type || "Expansion project"}</h2>
              <Button asChild size="sm" variant="outline" className="mt-3 border-white/25 text-white bg-white/5 hover:bg-white/10"><a href={`/compliance/${active.to_state}`}>View {active.to_state_name} compliance <ArrowRight className="w-4 h-4 ml-1" /></a></Button>
            </div>
            <div className="text-right">
              <p className="font-display text-4xl font-bold text-teal-400">{active.progress}%</p>
              <div className="mt-2 h-2 w-40 rounded-full bg-white/15 overflow-hidden"><div className="h-full bg-teal-400 transition-all" style={{ width: `${active.progress}%` }} /></div>
              <button onClick={() => remove(active.id)} data-testid="exp-delete" className="mt-3 text-xs text-red-300 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" /> Delete</button>
            </div>
          </div>

          <div className="space-y-6">
            {sections.map((sec) => {
              const tasks = active.tasks.filter((t) => t.section === sec);
              const done = tasks.filter((t) => t.done).length;
              return (
                <div key={sec} className="bg-white rounded-2xl p-5 border border-blue-900/5 shadow-sm">
                  <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold text-blue-950">{sec}</h3><span className="text-xs text-slate-500">{done}/{tasks.length}</span></div>
                  <div className="space-y-1.5">
                    {tasks.map((t) => (
                      <button key={t.id} data-testid={`task-${t.id}`} onClick={() => toggle(t.id)} className="w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 hover:bg-slate-50 transition-colors">
                        {t.done ? <CheckCircle2 className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" /> : <Circle className="w-5 h-5 text-slate-300 shrink-0 mt-0.5" />}
                        <span className={`text-sm ${t.done ? "text-slate-400 line-through" : "text-blue-950"}`}>{t.title}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>
      )}
    </div>
  );
}
